'use client'

import type { PayloadAdminBarProps, PayloadMeUser } from '@payloadcms/admin-bar'

import { cn } from '@/utilities/ui'
import { PayloadAdminBar } from '@payloadcms/admin-bar'
import { formatAdminURL } from 'payload/shared'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import './index.scss'

import {
  buildAdminBarDocumentQuery,
  getAdminBarLocale,
  resolveAdminBarTarget,
  type AdminBarTarget,
} from '@/lib/admin/adminBarTarget'
import { getPagePublicPath } from '@/lib/pages/fixedPageRoutes'
import { isFixedPageTemplateKey, type TemplatePathMap } from '@/lib/pages/pageTemplates'
import { getClientSideURL } from '@/utilities/getURL'

const baseClass = 'admin-bar'
const API_ROUTE = '/api'

const Title: React.FC = () => <span>Dashboard</span>

async function fetchTemplatePathMap(locale: string): Promise<Partial<TemplatePathMap>> {
  const params = new URLSearchParams({
    depth: '0',
    limit: '50',
    locale,
    'select[slug]': 'true',
    'select[templateKey]': 'true',
    'where[and][0][templateKey][not_equals]': 'default',
    'where[and][1][_status][equals]': 'published',
  })

  const response = await fetch(
    formatAdminURL({
      apiRoute: API_ROUTE,
      path: `/pages?${params.toString()}`,
    }),
    { credentials: 'include' },
  )

  if (!response.ok) return {}

  const result = (await response.json()) as {
    docs?: Array<{ slug?: string | null; templateKey?: string | null }>
  }
  const map: Partial<TemplatePathMap> = {}

  for (const doc of result.docs ?? []) {
    if (!isFixedPageTemplateKey(doc.templateKey)) continue
    map[doc.templateKey] = getPagePublicPath(doc.slug, doc.templateKey)
  }

  return map
}

async function fetchAdminBarDocumentId(target: AdminBarTarget): Promise<string | number | null> {
  const params = buildAdminBarDocumentQuery(target)

  const response = await fetch(
    formatAdminURL({
      apiRoute: API_ROUTE,
      path: `/${target.collection}?${params.toString()}`,
    }),
    { credentials: 'include' },
  )

  if (!response.ok) {
    return null
  }

  const result = (await response.json()) as { docs?: Array<{ id?: string | number }> }
  const doc = result.docs?.[0]

  return doc?.id ?? null
}

export const AdminBar: React.FC<{
  adminBarProps?: PayloadAdminBarProps
}> = (props) => {
  const { adminBarProps } = props || {}
  const pathname = usePathname()
  const [show, setShow] = useState(false)
  const [documentId, setDocumentId] = useState<string | number | undefined>(undefined)
  const [target, setTarget] = useState<AdminBarTarget | null>(null)
  const [templatePaths, setTemplatePaths] = useState<Partial<TemplatePathMap>>({})
  const [isResolving, setIsResolving] = useState(false)
  const barRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const onAuthChange = useCallback((user: PayloadMeUser) => {
    setShow(Boolean(user?.id))
  }, [])

  useEffect(() => {
    if (!show) return

    let cancelled = false
    const locale = getAdminBarLocale(pathname)

    void fetchTemplatePathMap(locale).then((paths) => {
      if (!cancelled) setTemplatePaths(paths)
    })

    return () => {
      cancelled = true
    }
  }, [pathname, show])

  useEffect(() => {
    if (!show) {
      setDocumentId(undefined)
      setTarget(null)
      setIsResolving(false)
      return
    }

    const nextTarget = resolveAdminBarTarget(pathname, templatePaths)

    setTarget(nextTarget)

    if (!nextTarget) {
      setDocumentId(undefined)
      setIsResolving(false)
      return
    }

    let cancelled = false
    setIsResolving(true)

    void fetchAdminBarDocumentId(nextTarget)
      .then((id) => {
        if (!cancelled) {
          setDocumentId(id ?? undefined)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsResolving(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [pathname, show, templatePaths])

  useEffect(() => {
    const el = barRef.current
    if (!el) return

    const updateVisibleHeight = () => {
      if (!show) {
        document.documentElement.style.setProperty('--admin-bar-height', '0px')
        return
      }

      const rect = el.getBoundingClientRect()
      const visibleHeight = Math.max(0, Math.min(rect.height, rect.bottom))
      document.documentElement.style.setProperty('--admin-bar-height', `${visibleHeight}px`)
    }

    updateVisibleHeight()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateVisibleHeight) : undefined

    if (resizeObserver) {
      resizeObserver.observe(el)
    }

    window.addEventListener('scroll', updateVisibleHeight, { passive: true })
    window.addEventListener('resize', updateVisibleHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('scroll', updateVisibleHeight)
      window.removeEventListener('resize', updateVisibleHeight)
      document.documentElement.style.removeProperty('--admin-bar-height')
    }
  }, [show])

  const collectionSlug = target?.collection ?? 'pages'
  const collectionLabels = target?.labels ?? { plural: 'Pages', singular: 'Page' }

  return (
    <div
      ref={barRef}
      className={cn(baseClass, 'relative z-[110] bg-black py-3 text-white', {
        block: show,
        hidden: !show,
      })}
    >
      <div className="page-container mx-auto max-w-[1680px] px-0 xl:px-4">
        <PayloadAdminBar
          {...adminBarProps}
          className="text-white"
          classNames={{
            controls: 'font-medium text-white',
            create: 'font-medium text-white',
            edit: 'font-medium text-white',
            logo: 'text-white',
            logout: 'font-medium text-white',
            preview: 'font-medium text-white',
            user: 'text-white',
          }}
          cmsURL={getClientSideURL()}
          collectionSlug={collectionSlug}
          collectionLabels={collectionLabels}
          id={documentId != null ? String(documentId) : undefined}
          logo={<Title />}
          onAuthChange={onAuthChange}
          onPreviewExit={() => {
            fetch('/next/exit-preview').then(() => {
              router.push('/')
              router.refresh()
            })
          }}
          style={{
            backgroundColor: 'transparent',
            padding: 0,
            position: 'relative',
            zIndex: 'unset',
          }}
        />
        {isResolving ? (
          <span className="sr-only" aria-live="polite">
            Resolving page editor link
          </span>
        ) : null}
      </div>
    </div>
  )
}
